// packages/kern/src/text.ts — Textsatz (SPEC §3.6). KEIN DOM.
//
// Außer `text-canvas.ts` darf keine Datei in `packages/kern/src` `document`,
// `window`, `navigator`, `fetch`, `localStorage` oder einen React-Import
// enthalten. Die einzige Browserabhängigkeit des Layouts ist hier zum
// eingespritzten Messer geworden.

import { trennstellen } from "./silben";
import { CHART_FAM } from "./styles";

export interface Messer {
  /** Breite von `text` in px, gesetzt in der CSS-Kurzform `font`. */
  breite(text: string, font: string): number;
}

export function fontStr(size: number, weight?: number, italic?: boolean): string {
  return (italic ? "italic " : "") + (weight || 400) + " " + size + "px " + CHART_FAM;
}

export function wrapText(text: string | null | undefined, maxW: number, font: string, m: Messer): string[] {
  const out: string[] = [];
  String(text == null ? "" : text).split("\n").forEach((para) => {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(""); return; }
    let line = words[0]!;
    for (let i = 1; i < words.length; i++) {
      const t = line + " " + words[i];
      if (m.breite(t, font) <= maxW) line = t;
      else { out.push(line); line = words[i]!; }
    }
    out.push(line);
  });
  return out;
}

/**
 * Wie `wrapText()`, aber ein Wort, das ALLEIN breiter ist als die Zeile, wird
 * an Silben getrennt (T45): „Daten-“ / „übergabe“. Ein Wort, das auf die
 * nächste Zeile passt, wandert dorthin und bleibt ganz — getrennt wird nur,
 * wo es sonst über den Kasten hinausstünde. Passt nicht einmal die erste
 * Silbe, bleibt der Rest stehen und läuft über (T41).
 *
 * Für Texte, in denen jedes Wort in die Zeile passt, ist das Ergebnis
 * zeilengleich mit `wrapText()`.
 */
export function wrapSilben(text: string | null | undefined, maxW: number, font: string, m: Messer): string[] {
  const passt = (s: string) => m.breite(s, font) <= maxW;
  const out: string[] = [];
  String(text == null ? "" : text).split("\n").forEach((para) => {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(""); return; }
    let line = "";
    for (const w of words) {
      const kand = line ? line + " " + w : w;
      if (passt(kand)) { line = kand; continue; }
      if (line) { out.push(line); line = ""; }
      if (passt(w)) { line = w; continue; }
      const stellen = trennstellen(w);
      let ab = 0;
      while (!passt(w.slice(ab))) {
        let kopf = "";
        let bis = -1;
        for (const s of stellen) {
          if (s.pos <= ab) continue;
          const k = w.slice(ab, s.pos) + (s.strich ? "-" : "");
          if (!passt(k)) break;
          kopf = k;
          bis = s.pos;
        }
        if (bis < 0) break;
        out.push(kopf);
        ab = bis;
      }
      line = w.slice(ab);
    }
    out.push(line);
  });
  return out;
}

export interface Satz { size: number; lines: string[]; lh: number }

/**
 * Kleinste Balkenschrift: 10 px = 7,5 pt bei 96 dpi (T41). Darunter wird
 * NICHT weiter geschrumpft — passt der Text dann nicht, läuft er über den
 * Kasten hinaus. Unlesbar klein ist schlimmer als übersteht: ein Überstand
 * fällt beim Gegenlesen auf und lässt sich beheben, ein Satz in 4,9 pt fällt
 * erst im Druck auf.
 *
 * Vorher lag die Grenze bei 6,5 px (≈ 4,9 pt).
 */
export const SCHRIFT_MIN_PX = 10;

/** Die 217 Codepunkte der drei Poppins-Subsets. Der Zahlenblock wird NICHT von Hand
 *  getippt: `python3 scripts/subset-liste.py --schreibe packages/kern/src/text.ts <woff2…>`
 *  erzeugt ihn aus den cmaps, und `scripts/schriften-pruefen.sh` diffed ihn in CI (R-24). */
export const SUBSET: ReadonlySet<number> = new Set([
  /* 217 Codepunkte, erzeugt von scripts/subset-liste.py — nicht bearbeiten */
  0x0000, 0x000D, 0x0020, 0x0021, 0x0022, 0x0023, 0x0024, 0x0025, 0x0026, 0x0027,
  0x0028, 0x0029, 0x002A, 0x002B, 0x002C, 0x002D, 0x002E, 0x002F, 0x0030, 0x0031,
  0x0032, 0x0033, 0x0034, 0x0035, 0x0036, 0x0037, 0x0038, 0x0039, 0x003A, 0x003B,
  0x003C, 0x003D, 0x003E, 0x003F, 0x0040, 0x0041, 0x0042, 0x0043, 0x0044, 0x0045,
  0x0046, 0x0047, 0x0048, 0x0049, 0x004A, 0x004B, 0x004C, 0x004D, 0x004E, 0x004F,
  0x0050, 0x0051, 0x0052, 0x0053, 0x0054, 0x0055, 0x0056, 0x0057, 0x0058, 0x0059,
  0x005A, 0x005B, 0x005C, 0x005D, 0x005E, 0x005F, 0x0060, 0x0061, 0x0062, 0x0063,
  0x0064, 0x0065, 0x0066, 0x0067, 0x0068, 0x0069, 0x006A, 0x006B, 0x006C, 0x006D,
  0x006E, 0x006F, 0x0070, 0x0071, 0x0072, 0x0073, 0x0074, 0x0075, 0x0076, 0x0077,
  0x0078, 0x0079, 0x007A, 0x007B, 0x007C, 0x007D, 0x007E, 0x00A0, 0x00A1, 0x00A2,
  0x00A3, 0x00A4, 0x00A5, 0x00A6, 0x00A7, 0x00A8, 0x00A9, 0x00AA, 0x00AB, 0x00AC,
  0x00AD, 0x00AE, 0x00AF, 0x00B0, 0x00B1, 0x00B2, 0x00B3, 0x00B4, 0x00B5, 0x00B6,
  0x00B7, 0x00B8, 0x00B9, 0x00BA, 0x00BB, 0x00BC, 0x00BD, 0x00BE, 0x00BF, 0x00C0,
  0x00C1, 0x00C2, 0x00C3, 0x00C4, 0x00C5, 0x00C6, 0x00C7, 0x00C8, 0x00C9, 0x00CA,
  0x00CB, 0x00CC, 0x00CD, 0x00CE, 0x00CF, 0x00D0, 0x00D1, 0x00D2, 0x00D3, 0x00D4,
  0x00D5, 0x00D6, 0x00D7, 0x00D8, 0x00D9, 0x00DA, 0x00DB, 0x00DC, 0x00DD, 0x00DE,
  0x00DF, 0x00E0, 0x00E1, 0x00E2, 0x00E3, 0x00E4, 0x00E5, 0x00E6, 0x00E7, 0x00E8,
  0x00E9, 0x00EA, 0x00EB, 0x00EC, 0x00ED, 0x00EE, 0x00EF, 0x00F0, 0x00F1, 0x00F2,
  0x00F3, 0x00F4, 0x00F5, 0x00F6, 0x00F7, 0x00F8, 0x00F9, 0x00FA, 0x00FB, 0x00FC,
  0x00FD, 0x00FE, 0x00FF, 0x0131, 0x0152, 0x0153, 0x02BC, 0x02C6, 0x02DA, 0x02DC,
  0x2013, 0x2014, 0x2018, 0x2019, 0x201A, 0x201C, 0x201D, 0x201E, 0x2022, 0x2026,
  0x2039, 0x203A, 0x2044, 0x20AC, 0x2122, 0x2212, 0x2215,
]);

/** Zeichen im Text, die KEINE der drei Schriftdateien kennt — eindeutig, in Reihenfolge.
 *  Fuer sie greift ein Rueckfall, der im Browser und im Abbild ein anderer ist; dort
 *  bricht der Export anders um als der Editor. */
export function fremdeZeichen(text: string): string[] {
  const aus: string[] = [];
  const gesehen = new Set<string>();
  for (const z of String(text == null ? "" : text)) {
    // Steuerzeichen sind Struktur, keine Glyphe: "\n" ist der harte Umbruch
    // aus `Eintrag.text` und wird von wrapText() selbst zerlegt.
    if (z === "\n" || z === "\r" || z === "\t") continue;
    const cp = z.codePointAt(0);
    if (cp === undefined || SUBSET.has(cp) || gesehen.has(z)) continue;
    gesehen.add(z);
    aus.push(z);
  }
  return aus;
}

/** Die Schrumpfschleife ist Teil des Druckbilds: `base` bis SCHRIFT_MIN_PX in
 *  Schritten von 0,5, Zeilenhöhe `size * 1.22`. Kein Math.round, keine
 *  Binärsuche, kein „Optimieren" — eine andere Schrittfolge liefert ein
 *  anderes Blatt.
 *
 *  Liegt schon `base` unter der Grenze (kleine `fontScale`), wird auf die
 *  Grenze ANGEHOBEN, nicht darunter gesetzt. */
export function fitText(
  text: string, maxW: number, maxH: number, base: number,
  weight: number | undefined, italic: boolean | undefined, m: Messer,
): Satz {
  let size = base;
  while (size > SCHRIFT_MIN_PX) {
    const f = fontStr(size, weight, italic);
    const lines = wrapText(text, maxW, f, m);
    const lh = size * 1.22;
    const widest = lines.reduce((mx, l) => Math.max(mx, m.breite(l, f)), 0);
    if (lines.length * lh <= maxH && widest <= maxW + 0.5) return { size, lines, lh };
    size -= 0.5;
  }
  // Untergrenze erreicht: nicht kleiner setzen. Ein Wort, das allein nicht
  // in die Zeile passt, wird jetzt an Silben getrennt (T45); passt es dann
  // immer noch nicht, steht der Text über den Kasten hinaus (T41).
  const f = fontStr(SCHRIFT_MIN_PX, weight, italic);
  return { size: SCHRIFT_MIN_PX, lines: wrapSilben(text, maxW, f, m), lh: SCHRIFT_MIN_PX * 1.22 };
}
